import React, { useRef } from "react";
import { motion } from "framer-motion";
import {
  Cpu,
  GraduationCap,
  HeartHandshake,
  Smartphone,
  BrainCircuit,
  Telescope,
  Sparkles,
  Globe,
  ShieldCheck,
  Speaker,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    id: "tech-innovation",
    num: "01",
    title: "Technology & AI Innovation",
    icon: Sparkles,
    tagline: "Modernization, end-to-end.",
    description:
      "Legacy stacks rewritten. Mobile experiences reimagined. Web platforms re-engineered. Security hardened. Audio-visual systems made intelligent. We modernize what slows you down.",
    span: "lg:col-span-12",
    accent: true,
    pillars: [
      {
        icon: Smartphone,
        title: "Mobile",
        copy: "Native iOS/Android, React Native, on-device AI, offline-first sync.",
      },
      {
        icon: Globe,
        title: "Web",
        copy: "Modern React/Next.js platforms, headless commerce, edge-rendered apps.",
      },
      {
        icon: ShieldCheck,
        title: "Security",
        copy: "Physical (AI cameras, LPR, access control) and IT/application security — unified.",
      },
      {
        icon: Speaker,
        title: "Audio-Visual",
        copy: "Smart conferencing, broadcast workflows, voice/video AI, signage.",
      },
    ],
  },
  {
    id: "ai-crm",
    num: "02",
    title: "AI CRM",
    icon: Cpu,
    tagline: "Customers, illuminated.",
    description:
      "A CRM that reads context, drafts outreach, scores intent, and closes loops — without spreadsheets, without nagging, without friction.",
    points: ["Conversational pipeline ops", "Predictive lead scoring", "Auto-drafted follow-ups"],
    span: "lg:col-span-7",
  },
  {
    id: "school",
    num: "03",
    title: "School Management",
    icon: GraduationCap,
    tagline: "Operations on autopilot.",
    description:
      "Attendance, fees, timetabling, parent comms — unified into a single intelligent dashboard for K-12 and universities.",
    points: ["Smart timetabling", "Parent comms automation", "Multi-campus admin"],
    span: "lg:col-span-5",
  },
  {
    id: "nonprofit",
    num: "04",
    title: "Non-Profit AI CRM",
    icon: HeartHandshake,
    tagline: "Donors, deeply understood.",
    description:
      "Donor journeys, grant tracking, volunteer rosters, and impact storytelling — engineered for missions that move the world.",
    points: ["Donor lifecycle AI", "Grant intelligence", "Impact reporting"],
    span: "lg:col-span-5",
  },
  {
    id: "mobile",
    num: "05",
    title: "Mobile Apps",
    icon: Smartphone,
    tagline: "Native. Native fast.",
    description:
      "iOS and Android apps engineered with React Native and Swift/Kotlin where it matters. Pixel craft meets AI inference at the edge.",
    points: ["React Native + Native modules", "On-device inference", "Offline-first sync"],
    span: "lg:col-span-7",
  },
  {
    id: "ai-dev",
    num: "06",
    title: "AI Development",
    icon: BrainCircuit,
    tagline: "Custom intelligence, end-to-end.",
    description:
      "We build the agents, retrieval pipelines, and fine-tuned models that turn your data into a moat. From prototype to production scale.",
    points: ["Agentic workflows", "RAG & vector systems", "LLM fine-tuning"],
    span: "lg:col-span-6",
  },
  {
    id: "research",
    num: "07",
    title: "AI Research",
    icon: Telescope,
    tagline: "The single-stride leap.",
    description:
      "Applied research partnerships for organizations chasing the frontier — multimodal models, reasoning systems, evaluation frameworks.",
    points: ["Applied research", "Evaluation harnesses", "White-paper grade output"],
    span: "lg:col-span-6",
  },
];

function ServiceCard({ service, index }) {
  const Icon = service.icon;
  const ref = useRef(null);
  const isPillarCard = Array.isArray(service.pillars);

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
      className={`bento-card flex flex-col justify-between min-h-[320px] ${service.span} ${
        service.accent ? "lg:min-h-[500px]" : ""
      }`}
      data-testid={`service-card-${service.id}`}
    >
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-8">
          <div
            className={`flex items-center justify-center border border-black/15 ${
              service.accent ? "w-16 h-16" : "w-12 h-12"
            }`}
          >
            <Icon className="text-[#C99B1A]" size={service.accent ? 28 : 22} strokeWidth={1.5} />
          </div>
          <span className="font-mono text-xs text-black/40">/{service.num}</span>
        </div>
        <h3
          className={`font-display tracking-tighter mb-3 text-[#0A0A0A] ${
            service.accent ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"
          }`}
        >
          {service.title}
        </h3>
        <p className="overline mb-5">{service.tagline}</p>
        <p
          className={`text-black/65 leading-relaxed ${
            service.accent ? "text-base md:text-lg max-w-xl" : "text-sm md:text-base"
          } ${isPillarCard ? "max-w-3xl" : ""}`}
        >
          {service.description}
        </p>
      </div>

      {isPillarCard ? (
        <div className="relative z-10 mt-10 pt-8 border-t border-black/10 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {service.pillars.map((p) => {
            const PIcon = p.icon;
            return (
              <div key={p.title} className="group/pillar">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center border border-black/15 group-hover/pillar:border-[#C99B1A]/60 transition-colors">
                    <PIcon size={16} className="text-[#C99B1A]" strokeWidth={1.6} />
                  </div>
                  <span className="font-display text-base text-[#0A0A0A] tracking-tight">{p.title}</span>
                </div>
                <p className="text-xs lg:text-sm text-black/60 leading-relaxed">{p.copy}</p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="relative z-10 mt-8 pt-6 border-t border-black/10">
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-black/55 font-mono">
            {service.points.map((p) => (
              <li key={p} className="flex items-center gap-2">
                <span className="text-[#C99B1A]">+</span>
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
        <ArrowUpRight size={18} className="text-[#C99B1A]" />
      </div>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="services" className="section relative bg-[#EDEAE0]" data-testid="services-section">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-20">
          <div className="lg:col-span-7">
            <div className="overline mb-6">[01] / Capabilities</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-7xl leading-[0.95] tracking-tighter text-[#0A0A0A]">
              Seven verticals.
              <br />
              <span className="text-[#C99B1A]">One brilliant</span> stack.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="text-black/65 text-base leading-relaxed">
              Each engagement begins with the same question: where can intelligence remove the most friction? We
              architect the answer — and ship it.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
